import { useState, useEffect, useCallback } from "react";

// Components
import Table from "./Childs/Table";
import Delete from "./Delete";
import Form from "./Childs/Form";
import Description from "../../Common/Childs/Structure/Description";
import Title from "../../Common/Childs/Structure/Title";
import FormCard from "../../Common/Childs/Structure/FormCard";

// Context
import { useGlobal } from "../../../context/GlobalContextProvider";

// Assets
import plus from "../../../assets/icons/plus-solid.svg";

// Constants
const INITIAL_FORM_STATE = {
  approved_name_id: 0,
  approved_name: "",
  art_pack_id: 0,
  display_order: 0,
};

const DESCRIPTION_TEXT = "Create a list of approved names for NFTs minted using this Art Pack.";

// API Service
const approvedNameAPI = {
  getAll: async (artPackId) => {
    const res = await fetch(`/api/artpack/${artPackId}/approvedname`);
    if (res.status === 200) {
      return res.json();
    }
    return [];
  },

  create: async (artPackId, data) => {
    return fetch(`/api/artpack/${artPackId}/approvedname`, {
      method: "POST",
      body: JSON.stringify({
        approved_name: data.approved_name,
        display_order: data.display_order,
        art_pack_id: artPackId,
      }),
      headers: { "Content-type": "application/json; charset=UTF-8" },
    });
  },

  update: async (artPackId, id, data) => {
    return fetch(`/api/artpack/${artPackId}/approvedname/${id}`, {
      method: "PUT",
      body: JSON.stringify({
        approved_name: data.approved_name,
        display_order: data.display_order,
        art_pack_id: artPackId,
      }),
      headers: { "Content-type": "application/json; charset=UTF-8" },
    });
  },

  delete: async (artPackId, id) => {
    return fetch(`/api/artpack/${artPackId}/approvedname/${id}`, {
      method: "DELETE",
      headers: { "Content-type": "application/json; charset=UTF-8" },
    });
  },
};


const Index = () => {
  const { artPack, setSpinner } = useGlobal();

  const [approvedNames, setApprovedNames] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [isDeleteMode, setIsDeleteMode] = useState(false);

  // Normalize approved name data
  const normalizeData = useCallback((items) => {
    return items.map((el) => ({
      approved_name_id: el.approved_name_id,
      approved_name: el.approved_name,
      art_pack_id: el.art_pack_id,
      display_order: el.display_order,
    }));
  }, []);

  // Load approved names from API
  const loadApprovedNames = useCallback(async () => {
    if (!artPack?.art_pack_id) return;

    setSpinner(true);
    try {
      const data = await approvedNameAPI.getAll(artPack.art_pack_id);
      setApprovedNames(normalizeData(data));
    } catch (error) {
      console.error("Error loading approved names:", error);
    } finally {
      setSpinner(false);
    }
  }, [artPack?.art_pack_id, setSpinner, normalizeData]);

  // Load data when art pack changes
  useEffect(() => {
    loadApprovedNames();
  }, [loadApprovedNames]);

  // Reset form and close
  const resetForm = useCallback(() => {
    setShowForm(false);
    setIsDeleteMode(false);
    setFormData(INITIAL_FORM_STATE);
  }, []);

  // Open form for new approved name
  const handleAddNew = useCallback(() => {
    setFormData(INITIAL_FORM_STATE);
    setShowForm(true);
    setIsDeleteMode(false);
  }, []);

  // Open form for editing
  const handleEdit = useCallback((data) => {
    setFormData(data);
    setShowForm(true);
    setIsDeleteMode(false);
  }, []);

  // Open delete confirmation
  const handleDelete = useCallback((data) => {
    setFormData(data);
    setShowForm(false);
    setIsDeleteMode(true);
  }, []);

  // Create new approved name
  const handleCreate = useCallback(async (data) => {
    setSpinner(true);
    try {
      await approvedNameAPI.create(artPack.art_pack_id, data);
      await loadApprovedNames();
      resetForm();
    } catch (error) {
      console.error("Error creating approved name:", error);
    } finally {
      setSpinner(false);
    }
  }, [artPack?.art_pack_id, loadApprovedNames, resetForm, setSpinner]);

  // Update existing approved name
  const handleUpdate = useCallback(async (data) => {
    setSpinner(true);
    try {
      await approvedNameAPI.update(artPack.art_pack_id, data.id, data);
      await loadApprovedNames();
      resetForm();
    } catch (error) {
      console.error("Error updating approved name:", error);
    } finally {
      setSpinner(false);
    }
  }, [artPack?.art_pack_id, loadApprovedNames, resetForm, setSpinner]);

  // Delete approved name
  const handleDeleteConfirm = useCallback(async () => {
    setSpinner(true);
    try {
      await approvedNameAPI.delete(artPack.art_pack_id, formData.id);
      await loadApprovedNames();
      resetForm();
    } catch (error) {
      console.error("Error deleting approved name:", error);
    } finally {
      setSpinner(false);
    }
  }, [artPack?.art_pack_id, formData.id, loadApprovedNames, resetForm, setSpinner]);

  // Render delete confirmation
  if (isDeleteMode && formData.id) {
    return (
      <Delete
        onDiscard={resetForm}
        handleSubmit={handleDeleteConfirm}
        formData={formData}
      />
    );
  }

  // Render form
  if (showForm) {
    const isEditMode = Boolean(formData.approved_name);

    return (
      <>
        <Title title="Approved Names" />
        <Description text={DESCRIPTION_TEXT} />
        <FormCard>
          <Form
            handleSubmit={isEditMode ? handleUpdate : handleCreate}
            formData={formData}
            onDiscard={resetForm}
            typeCheck={isEditMode ? 'updateItem' : 'createItem'}
            artPackId={artPack?.art_pack_id}
          />
        </FormCard>
      </>
    );
  }

  // Render main list view
  return (
    <>
      <Title title="Approved Names" />
      <Description text={DESCRIPTION_TEXT} />
      {!artPack?.approved && (
        <div className="add-btn" onClick={handleAddNew}>
          Add Approved Name
          <img src={plus} className="add-icon" alt="Add" />
        </div>
      )}
      <Table
        data={approvedNames}
        onEdit={handleEdit}
        onDelete={handleDelete}
        setShowForm={setShowForm}
        setFormData={setFormData}
        isAllowedToEditOrDelete={!artPack?.approved}
      />
    </>
  );
};

export default Index;
